import { memo, PropsWithoutRef, useCallback } from 'react'

import { AutoDeleteOutlined, Delete } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { Row, Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { useVideoStreamDeleteMutation } from 'hooks/mutations/content'
import { useConfirm } from 'hooks/utils'

type SelectionToolbarActionProps = {
  selectedRows: Row<VideoStream>[]
  tableInstance: Table<VideoStream>
  onLifecyclePolicyClick?: () => void
}

const SelectionToolbarActionsRaw = ({
  selectedRows,
  tableInstance,
  onLifecyclePolicyClick,
}: PropsWithoutRef<SelectionToolbarActionProps>) => {
  const { t } = useTranslation(['common', 'domain'])

  const confirm = useConfirm()

  const { mutateAsync: deleteVideoStream } = useVideoStreamDeleteMutation()

  const handleDeleteSelectedVideoStreams = useCallback(async () => {
    try {
      await confirm(t('areYouSure'))

      await Promise.allSettled(selectedRows.map((row) => deleteVideoStream({ streamId: row.original.id })))

      tableInstance.resetRowSelection()
    } catch (_) {}
  }, [confirm, deleteVideoStream, selectedRows, t, tableInstance])

  return (
    <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <Tooltip
        title={t('domain:Workspace.Video.setContentLifecyclePolicy', { count: selectedRows.length })}
        placement="top"
      >
        <IconButton sx={{ ml: 'auto', mr: 0.5, my: 'auto' }} size="medium" onClick={onLifecyclePolicyClick}>
          <AutoDeleteOutlined fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('domain:Workspace.Video.deleteStream', { count: selectedRows.length })} placement="top">
        <IconButton sx={{ ml: 'auto', mr: 2, my: 'auto' }} size="medium" onClick={handleDeleteSelectedVideoStreams}>
          <Delete fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export const SelectionToolbarActions = memo(SelectionToolbarActionsRaw)
